
import { useScrollAnimation, getAnimationClass } from '../../../hooks/useScrollAnimation';

function AnimatedSection({ children, animation = 'slide-up' as 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in', className = '' }: { children: React.ReactNode; animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale-in'; className?: string }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div ref={ref} className={`${getAnimationClass(isVisible, animation)} ${className}`}>
      {children}
    </div>
  );
}

export default function OurStory() {
  const milestones = [
    { year: '2023 Q1', title: 'Nace la idea', desc: 'Un grupo de oftalmólogos y desarrolladores identifican la necesidad de un software especializado para la gestión clínica oftalmológica moderna.' },
    { year: '2023 Q3', title: 'Fundación de La Millefera Tech LLC', desc: 'Se constituye oficialmente la empresa con la visión de crear un ecosistema SaaS para la salud visual.' },
    { year: '2024 Q1', title: 'Lanzamiento de Anamnesy Soft', desc: 'La primera versión de Anamnesy Soft sale al mercado con funcionalidades de gestión clínica, historiales digitales y agenda inteligente.' },
    { year: '2024 Q3', title: 'Módulo de Óptica Integrado', desc: 'Se lanza el módulo de óptica con gestión de inventario, catálogo de monturas y lentes, y promociones automáticas.' },
    { year: '2025 Q1', title: '500+ profesionales activos', desc: 'Anamnesy Soft alcanza más de 500 profesionales activos en 12 países, gestionando más de 50,000 pacientes mensuales.' },
    { year: '2025', title: 'Expansión del ecosistema', desc: 'Desarrollo de nuevos módulos SaaS para otras especialidades médicas, ampliando el ecosistema de La Millefera Tech.' },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <AnimatedSection animation="slide-left">
            <div className="space-y-6 lg:sticky lg:top-32">
              <span className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-widest">Nuestra Historia</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                De una necesidad real a una <span className="font-display italic text-emerald-600">solución global</span>
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                La Millefera Tech LLC surgió de la frustración compartida por oftalmólogos que lidiaban con sistemas obsoletos, historiales en papel y procesos manuales que consumían horas valiosas de atención al paciente.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Nuestro equipo fundador, compuesto por profesionales de la salud visual y expertos en tecnología, decidió crear la herramienta que siempre quisieron tener: un software que realmente entendiera las necesidades de una consulta oftalmológica.
              </p>
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-100">
                <div className="w-full h-64">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20technology%20startup%20team%20working%20together%20in%20bright%20open%20office%20space%20with%20large%20monitors%20showing%20medical%20software%20dashboards%20emerald%20green%20accent%20colors%20collaborative%20creative%20atmosphere%20professional%20photography&width=600&height=350&seq=about-story-team-1&orientation=landscape"
                    alt="Equipo La Millefera Tech"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection animation="slide-right">
            <div className="space-y-0">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative pl-8 pb-10 last:pb-0">
                  <div className="absolute left-0 top-1.5 w-3 h-3 bg-emerald-500 rounded-full z-10"></div>
                  {index < milestones.length - 1 && (
                    <div className="absolute left-[5px] top-4 w-0.5 h-full bg-emerald-100"></div>
                  )}
                  <div className="bg-gray-50 rounded-xl p-5 hover:bg-emerald-50/50 transition-colors cursor-pointer">
                    <span className="text-[11px] font-bold text-emerald-600 uppercase tracking-wider">{milestone.year}</span>
                    <h3 className="text-base font-bold text-gray-900 mt-1 mb-2">{milestone.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{milestone.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
